import React from 'react';

import { CursorEditor, getPresenceColor, isNotNull } from '@aline/common';
import { useTheme } from '@mui/material';
import { getCursorRange } from '@slate-yjs/react';
import { useSnackbar } from 'notistack';
import { AlineAvatar, AlineAvatarGroup, AlineTooltip } from 'src/components';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { ReactEditor, useTypedEditor } from 'src/layout-engine';
import { useRemoteCursorStatesSelector } from 'src/layout-engine/hooks/remote-selection';
import { v4 as uuid4 } from 'uuid';

export function PresenceAvatars(): JSX.Element | null {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const editor = useTypedEditor(CursorEditor.isCursorEditor);
  const currentUserId = useCurrentUserId();
  const presences = useRemoteCursorStatesSelector(state =>
    Object.fromEntries(
      Object.entries(state)
        .map(([clientId, { data }]) =>
          data?.id ? ([data.id, { ...data, clientId }] as const) : null
        )
        .filter(entry => isNotNull(entry))
        .sort(([id1], [id2]) => (id1 < id2 ? -1 : 1))
    )
  );

  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  function scrollToCaret(presence: { clientId: string; displayName?: string }) {
    if (!ReactEditor.isReactEditor(editor)) return;

    const cursorState = CursorEditor.cursorState(editor, parseInt(presence.clientId, 10));
    if (!cursorState?.relativeSelection) return;

    const range = getCursorRange(editor, cursorState);
    if (range) {
      ClientTransforms.scrollToLocation(editor, {
        cMLocation: range,
        rWHeightPx,
        rWOffsetPx,
        select: false,
      });
      enqueueSnackbar({
        key: uuid4(),
        message: `Navigated to ${presence.displayName ?? 'a remote user'}.`,
        variant: 'success',
      });
    }
  }

  if (Object.keys(presences).length === 0) return null;

  return (
    <AlineAvatarGroup max={5}>
      {Object.entries(presences).map(([id, presence]) => (
        <AlineTooltip
          key={id}
          arrow
          placement="bottom"
          title={presence.displayName ?? 'A remote user'}
        >
          <AlineAvatar
            avatarInfo={{
              __typename: 'User',
              avatarColor: presence.avatarColor ?? theme.appColors.primary,
              displayName: presence.displayName ?? 'A remote user',
            }}
            style={{
              borderColor: presence.id
                ? getPresenceColor(presence.id, { currentUserId })
                : theme.palette.common.white,
              borderStyle: 'solid',
              borderWidth: 2,
              cursor: 'pointer',
            }}
            onClick={() => scrollToCaret(presence)}
          />
        </AlineTooltip>
      ))}
    </AlineAvatarGroup>
  );
}
